// 일정 상세의 "팀 편성" 섹션 — 팀 자동 편성 CTA / 팀 카드 / 멤버 칩.
//
// team mode 이벤트에서만 의미가 있다. 팀이 비어 있고 운영진+참가자 4명+면
// "팀 편성하기" CTA만 보여주고, 팀이 있으면 팀별 카드와 멤버 칩(이동/스케줄)을 그린다.

import { api } from "../api";
import { canSeeNtrp } from "../auth";
import { matchPlayerName } from "../matches";
import { formatNtrp } from "../ui/format";
import { mountPreact } from "../ui/preact-mount";
import { closeSheet, openSheet, toast } from "../ui/sheet";
import { buildParticipantScheduleView } from "./participantSchedule";

interface ParticipantLike {
  event_participant_id: string;
}

interface TeamMember {
  event_participant_id: string;
  participant?: {
    gender_snapshot?: string;
    ntrp_snapshot?: number | string | null;
  };
}

interface TeamLike {
  team_id: string;
  team_name?: string | null;
  team_order?: number;
  member_count?: number;
  average_ntrp?: number | null;
  members?: TeamMember[];
}

export interface TeamsSectionParams {
  ev: any;
  teams: TeamLike[];
  participants: ParticipantLike[];
  matches: any[];
  roundMap: Record<number, any>;
  canManage: boolean;
  hasMatches: boolean;
  onChanged: () => void;
}

interface MemberChipProps {
  member: { pid: string; name: string; gender: string; ntrp: any };
  team: TeamLike;
  otherTeam: TeamLike | undefined;
  participants: ParticipantLike[];
  matches: any[];
  roundMap: Record<number, any>;
  canEditTeam: boolean;
  hasMatches: boolean;
  evId: string;
  onChanged: () => void;
}

function MemberChip({
  member, team, otherTeam, participants, matches, roundMap,
  canEditTeam, hasMatches, evId, onChanged,
}: MemberChipProps) {
  const canMove = canEditTeam && !!otherTeam;
  const participant = hasMatches
    ? participants.find((p) => p.event_participant_id === member.pid)
    : null;
  const canViewSchedule = !!participant;
  const otherLabel = otherTeam?.team_name || "상대팀";

  async function move() {
    try {
      await api(`/api/v1/events/${evId}/teams/move-member`, {
        method: "POST",
        body: { event_participant_id: member.pid, from_team_id: team.team_id, to_team_id: otherTeam!.team_id },
      });
      toast(`${member.name} → ${otherLabel}`);
      onChanged();
    } catch (err: any) { toast(err.message); }
  }

  if (canMove && canViewSchedule) {
    return (
      <button
        type="button"
        class="team-member-chip team-member-chip--tappable"
        onClick={() => {
          const body = document.createElement("div");
          body.className = "stack";
          body.style.gap = "12px";
          const scheduleContent = buildParticipantScheduleView(participant!, matches, roundMap);
          if (scheduleContent) body.append(scheduleContent);
          const hr = document.createElement("hr");
          hr.style.cssText = "border:none;border-top:1px solid var(--border);margin:4px 0";
          body.append(hr);
          const moveBtn = document.createElement("button");
          moveBtn.type = "button";
          moveBtn.className = "btn btn--ghost btn--block";
          moveBtn.textContent = `${otherLabel}으로 이동`;
          moveBtn.onclick = async () => { await move(); closeSheet(); };
          body.append(moveBtn);
          openSheet(member.name, body);
        }}
      >
        {member.name}
      </button>
    );
  }
  if (canMove) {
    return (
      <button
        type="button"
        class="team-member-chip team-member-chip--editable"
        onClick={move}
      >
        {member.name}
      </button>
    );
  }
  if (canViewSchedule) {
    return (
      <button
        type="button"
        class="team-member-chip team-member-chip--tappable"
        onClick={() => {
          const body = buildParticipantScheduleView(participant!, matches, roundMap);
          if (body) openSheet(`${matchPlayerName(participant)} 스케줄`, body);
        }}
      >
        {member.name}
      </button>
    );
  }
  return <span class="team-member-chip">{member.name}</span>;
}

function TeamCard({
  team, otherTeam, participants, matches, roundMap,
  canEditTeam, hasMatches, evId, onChanged,
}: {
  team: TeamLike;
  otherTeam: TeamLike | undefined;
  participants: ParticipantLike[];
  matches: any[];
  roundMap: Record<number, any>;
  canEditTeam: boolean;
  hasMatches: boolean;
  evId: string;
  onChanged: () => void;
}) {
  const tmembers = (team.members || []).map((m) => ({
    pid: m.event_participant_id,
    name: matchPlayerName(m),
    gender: m.participant?.gender_snapshot || "unknown",
    ntrp: m.participant?.ntrp_snapshot,
  }));
  const males = tmembers.filter((m) => m.gender === "male");
  const females = tmembers.filter((m) => m.gender !== "male");
  const showNtrpAvg = canSeeNtrp() && team.average_ntrp && tmembers.some((m) => m.ntrp != null);

  return (
    <div class="team-card">
      <div class="team-card__header">
        <span class="team-card__name">{team.team_name || `팀 ${team.team_order}`}</span>
        <span class="team-card__meta">
          {(team.member_count || tmembers.length)}명{showNtrpAvg ? ` · NTRP ${formatNtrp(team.average_ntrp!)}` : ""}
        </span>
      </div>
      {males.length > 0 && (
        <div class="team-card__group">
          <span class="team-card__gender">남</span>
          <div class="team-card__members">
            {males.map((m) => (
              <MemberChip
                key={m.pid}
                member={m}
                team={team}
                otherTeam={otherTeam}
                participants={participants}
                matches={matches}
                roundMap={roundMap}
                canEditTeam={canEditTeam}
                hasMatches={hasMatches}
                evId={evId}
                onChanged={onChanged}
              />
            ))}
          </div>
        </div>
      )}
      {females.length > 0 && (
        <div class="team-card__group">
          <span class="team-card__gender">여</span>
          <div class="team-card__members">
            {females.map((m) => (
              <MemberChip
                key={m.pid}
                member={m}
                team={team}
                otherTeam={otherTeam}
                participants={participants}
                matches={matches}
                roundMap={roundMap}
                canEditTeam={canEditTeam}
                hasMatches={hasMatches}
                evId={evId}
                onChanged={onChanged}
              />
            ))}
          </div>
        </div>
      )}
      {canEditTeam && otherTeam && (
        <p class="field__hint" style="margin-top:8px">이름을 탭하면 상대팀으로 이동해요</p>
      )}
    </div>
  );
}

function TeamsSection(params: TeamsSectionParams) {
  const { ev, teams, participants, matches, roundMap, canManage, hasMatches, onChanged } = params;
  const isTeamMode = ev.mode === "team";
  const beforeStart = ev.status === "draft" || ev.status === "ready";
  const canEditTeam = canManage && beforeStart;

  if (isTeamMode && teams.length === 0 && canEditTeam && participants.length >= 4) {
    const hint = ev.is_friendly_match
      ? "클럽 회원은 우리 팀, 게스트는 상대 팀으로 자동 배정돼요."
      : "NTRP 기반으로 균형 있게 자동 편성돼요.";
    return (
      <div class="section section--panel">
        <div class="section__title"><h3>팀 편성</h3></div>
        <p class="muted small">{hint}</p>
        <button
          type="button"
          class="btn btn--primary btn--block"
          style="margin-top:12px"
          onClick={async () => {
            try {
              await api(`/api/v1/events/${ev.event_id}/teams/generate`, { method: "POST" });
              toast("팀이 편성되었어요");
              onChanged();
            } catch (err: any) { toast(err.message); }
          }}
        >
          팀 편성하기
        </button>
      </div>
    );
  }

  if (teams.length === 0) return null;

  const assignedPids = new Set(
    teams.flatMap((t) => (t.members || []).map((m) => m.event_participant_id)),
  );
  const unassigned = participants.filter((p) => !assignedPids.has(p.event_participant_id));

  return (
    <div class="section">
      <div class="section__title">
        <h3>팀 편성</h3>
        <span class="count">{teams.length}팀</span>
      </div>
      {unassigned.length > 0 && canEditTeam && (
        <div class="stale-notice">
          {unassigned.map((p) => matchPlayerName(p)).join(", ")}이(가) 아직 팀에 배정되지 않았어요. 팀을 재편성해주세요.
        </div>
      )}
      {teams.map((team) => {
        const otherTeam = teams.find((t) => t.team_id !== team.team_id);
        return (
          <TeamCard
            key={team.team_id}
            team={team}
            otherTeam={otherTeam}
            participants={participants}
            matches={matches}
            roundMap={roundMap}
            canEditTeam={canEditTeam}
            hasMatches={hasMatches}
            evId={ev.event_id}
            onChanged={onChanged}
          />
        );
      })}
      {canEditTeam && (
        <button
          type="button"
          class="btn btn--ghost btn--sm"
          style="margin-top:8px"
          onClick={async () => {
            if (!confirm("팀을 다시 자동 편성할까요? 현재 팀 구성이 초기화돼요.")) return;
            try {
              await api(`/api/v1/events/${ev.event_id}/teams/generate`, { method: "POST" });
              toast("팀이 재편성되었어요");
              onChanged();
            } catch (err: any) { toast(err.message); }
          }}
        >
          팀 재편성
        </button>
      )}
    </div>
  );
}

export function buildTeamsSection(params: TeamsSectionParams): HTMLElement | null {
  const { ev, teams, participants, canManage } = params;
  const isTeamMode = ev.mode === "team";
  const beforeStart = ev.status === "draft" || ev.status === "ready";
  const canEditTeam = canManage && beforeStart;
  const showCta = isTeamMode && teams.length === 0 && canEditTeam && participants.length >= 4;
  if (!showCta && teams.length === 0) return null;
  return mountPreact(<TeamsSection {...params} />);
}
